export interface AnchoredPosition {
  readonly left: number;
  readonly top: number;
  readonly width: number;
}

export const UNMEASURED_ANCHORED_POSITION: AnchoredPosition = {
  left: 0,
  top: 0,
  width: 0,
};

export interface AnchoredPositionInput {
  readonly floatingHeight: number;
  readonly floatingWidth?: number;
  readonly gap: number;
  readonly minWidth: number;
  readonly placement: "start" | "end";
  readonly triggerBounds: TriggerBounds;
  readonly viewportHeight: number;
  readonly viewportPadding: number;
  readonly viewportWidth: number;
}

export interface AnchoredWidthInput {
  readonly minWidth: number;
  readonly triggerWidth: number;
  readonly viewportPadding: number;
  readonly viewportWidth: number;
}

interface AnchoredLeftInput {
  readonly position: AnchoredPositionInput;
  readonly width: number;
}

interface TriggerBounds {
  readonly bottom: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly width: number;
}

export function calculateAnchoredPosition(
  props: AnchoredPositionInput,
): AnchoredPosition {
  const width = calculateAnchoredWidth({
    minWidth: props.minWidth,
    triggerWidth: props.triggerBounds.width,
    viewportPadding: props.viewportPadding,
    viewportWidth: props.viewportWidth,
  });
  const renderedWidth = Math.min(
    Math.max(props.floatingWidth ?? width, width),
    Math.max(0, props.viewportWidth - props.viewportPadding * 2),
  );
  return {
    left: getLeft({ position: props, width: renderedWidth }),
    top: getTop(props),
    width,
  };
}

export function calculateAnchoredWidth(props: AnchoredWidthInput): number {
  const availableWidth = Math.max(
    0,
    props.viewportWidth - props.viewportPadding * 2,
  );
  return Math.min(Math.max(props.triggerWidth, props.minWidth), availableWidth);
}

function getLeft(props: AnchoredLeftInput): number {
  const desiredLeft =
    props.position.placement === "end"
      ? props.position.triggerBounds.right - props.width
      : props.position.triggerBounds.left;
  const maxLeft = Math.max(
    props.position.viewportPadding,
    props.position.viewportWidth - props.width - props.position.viewportPadding,
  );
  return Math.min(
    Math.max(desiredLeft, props.position.viewportPadding),
    maxLeft,
  );
}

function getTop(props: AnchoredPositionInput): number {
  const topBelow = props.triggerBounds.bottom + props.gap;
  const topAbove = props.triggerBounds.top - props.floatingHeight - props.gap;
  const fitsBelow =
    topBelow + props.floatingHeight <=
    props.viewportHeight - props.viewportPadding;
  const fitsAbove = topAbove >= props.viewportPadding;
  const spaceBelow =
    props.viewportHeight - props.triggerBounds.bottom - props.gap;
  const spaceAbove = props.triggerBounds.top - props.gap;
  const shouldOpenBelow = fitsBelow || (!fitsAbove && spaceBelow >= spaceAbove);
  const desiredTop = shouldOpenBelow ? topBelow : topAbove;
  const maxTop = Math.max(
    props.viewportPadding,
    props.viewportHeight - props.floatingHeight - props.viewportPadding,
  );
  return Math.min(Math.max(desiredTop, props.viewportPadding), maxTop);
}
